import React, { createContext, useState, useEffect, useCallback } from 'react';

export const AppContext = createContext();

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

const DEFAULT_SITE_NAME = 'Parish Recorder';

// Routes that have hardcoded titles and should not be overwritten by AppContext
const ROUTES_WITH_CUSTOM_TITLES = ['/features'];

const DEFAULT_PLOT_COLORS = {
  noContacts: '#6b7280',     // Grey - plots with no contacts
  hasContacts: '#22c55e',    // Green - plots with contacts but none interred
  hasInterred: '#3b82f6',    // Blue - plots with interred contacts
  highlight: '#fbbf24'       // Amber - temporary highlight color
};

const DEFAULT_HEADER_LOGO = '/parish-recorder.svg';
const DEFAULT_LOGIN_LOGO = '/parish-recorder-dark-lg.png';

const DEFAULT_DEMO_MODE = {
  enabled: false,
  cutoffDate: null,
  message: null
};

export function AppProvider({ children }) {
  const [token, setToken] = useState(() => getCookie('token'));
  const [user, setUser] = useState(null);
  const [cemeteries, setCemeteries] = useState([]);
  const [currentCemetery, setCurrentCemetery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteName, setSiteName] = useState(DEFAULT_SITE_NAME);
  const [isSiteConfigured, setIsSiteConfigured] = useState(false);
  const [plotColors, setPlotColors] = useState(DEFAULT_PLOT_COLORS);
  const [headerLogo, setHeaderLogo] = useState(DEFAULT_HEADER_LOGO);
  const [loginLogo, setLoginLogo] = useState(DEFAULT_LOGIN_LOGO);
  const [showPlotSize, setShowPlotSize] = useState(false);
  const [demoMode, setDemoMode] = useState(DEFAULT_DEMO_MODE);
  const [portalEnabled, setPortalEnabled] = useState(false);
  const [googleTagId, setGoogleTagId] = useState('');
  const [googleTagEnvOverride, setGoogleTagEnvOverride] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [tenantPlan, setTenantPlan] = useState('enterprise');
  const [availableFeatures, setAvailableFeatures] = useState({});
  const [tenantStatus, setTenantStatus] = useState({ isActive: true, suspendedAt: null, suspendReason: null });

  const expireToken = useCallback(() => {
    setToken(null);
    setUser(null);
    setCemeteries([]);
    setCurrentCemetery(null);
    setTenantId(null);
    setTenantPlan('enterprise');
    setAvailableFeatures({});
    document.cookie = `token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }, []);

  // Fetch site settings on mount
  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await fetch('/api/site-settings');
      if (response.ok) {
        const data = await response.json();
        setSiteName(data.siteName || DEFAULT_SITE_NAME);
        setIsSiteConfigured(data.isConfigured || false);
        setPlotColors(data.plotColors || DEFAULT_PLOT_COLORS);
        setHeaderLogo(data.headerLogo || DEFAULT_HEADER_LOGO);
        setLoginLogo(data.loginLogo || DEFAULT_LOGIN_LOGO);
        setShowPlotSize(data.showPlotSize || false);
        setDemoMode(data.demoMode || DEFAULT_DEMO_MODE);
        setGoogleTagId(data.googleTagId || '');
        setGoogleTagEnvOverride(data.googleTagEnvOverride || false);
        if (data.tenantPlan) setTenantPlan(data.tenantPlan);
        if (data.availableFeatures) setAvailableFeatures(data.availableFeatures);
        if (data.tenantStatus) setTenantStatus(data.tenantStatus);
        // Skip title update for routes with hardcoded titles
        if (!ROUTES_WITH_CUSTOM_TITLES.includes(window.location.pathname)) {
          document.title = data.siteName || DEFAULT_SITE_NAME;
        }
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchSiteSettings();
  }, [fetchSiteSettings]);

  // Fetch public portal config
  const fetchPortalConfig = useCallback(async () => {
    try {
      const response = await fetch('/api/public/config');
      if (response.ok) {
        const data = await response.json();
        setPortalEnabled(data.portalEnabled || false);
      }
    } catch (error) {
      console.error('Error fetching portal config:', error);
    }
  }, []);

  useEffect(() => {
    fetchPortalConfig();
  }, [fetchPortalConfig]);

  // Update document title when siteName changes (skip routes with hardcoded titles)
  useEffect(() => {
    if (!ROUTES_WITH_CUSTOM_TITLES.includes(window.location.pathname)) {
      document.title = siteName;
    }
  }, [siteName]);

  // Inject Google tag (gtag.js) when googleTagId is set
  useEffect(() => {
    const GTAG_SCRIPT_ID = 'gtag-js';
    const GTAG_INIT_ID = 'gtag-init';

    // Remove existing scripts if tag ID changed or was cleared
    const existingScript = document.getElementById(GTAG_SCRIPT_ID);
    const existingInit = document.getElementById(GTAG_INIT_ID);
    if (existingScript) existingScript.remove();
    if (existingInit) existingInit.remove();

    if (!googleTagId) return;

    // Validate format: G-XXXXXXXXXX or GT-XXXXXXXXXX or AW-XXXXXXXXXX
    if (!/^(G|GT|AW)-[A-Za-z0-9]+$/.test(googleTagId)) return;

    // Load gtag.js
    const script = document.createElement('script');
    script.id = GTAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleTagId)}`;
    document.head.appendChild(script);

    // Initialize gtag
    const initScript = document.createElement('script');
    initScript.id = GTAG_INIT_ID;
    initScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${googleTagId}');
    `;
    document.head.appendChild(initScript);
  }, [googleTagId]);

  // Fetch current user on mount or token change
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setCemeteries(data.cemeteries || []);
          setTenantId(data.user?.tenantId || null);
          if (data.tenantPlan) setTenantPlan(data.tenantPlan);
          if (data.availableFeatures) setAvailableFeatures(data.availableFeatures);

          // Set current cemetery from localStorage or first available
          const savedCemeteryId = localStorage.getItem('currentCemeteryId');
          const savedCemetery = data.cemeteries?.find(c => c.id === parseInt(savedCemeteryId));
          if (savedCemetery) {
            setCurrentCemetery(savedCemetery);
          } else if (data.cemeteries?.length > 0) {
            setCurrentCemetery(data.cemeteries[0]);
          }
        } else {
          // Token invalid, clear it
          expireToken();
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        expireToken();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token, expireToken]);

  // Save current cemetery to localStorage
  useEffect(() => {
    if (currentCemetery) {
      localStorage.setItem('currentCemeteryId', currentCemetery.id);
    }
  }, [currentCemetery]);

  const handleLogin = useCallback((loginData) => {
    setToken(loginData.token);
    setUser(loginData.user);
    setCemeteries(loginData.cemeteries || []);
    setTenantId(loginData.user?.tenantId || null);
    if (loginData.tenantPlan) setTenantPlan(loginData.tenantPlan);
    if (loginData.availableFeatures) setAvailableFeatures(loginData.availableFeatures);
    if (loginData.cemeteries?.length > 0) {
      setCurrentCemetery(loginData.cemeteries[0]);
    }
  }, []);

  const hasFeature = useCallback((feature) => {
    return availableFeatures[feature] !== false;
  }, [availableFeatures]);

  return (
    <AppContext.Provider value={{
      token,
      setToken,
      expireToken,
      user,
      setUser,
      cemeteries,
      setCemeteries,
      currentCemetery,
      setCurrentCemetery,
      handleLogin,
      loading,
      isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
      isSuperAdmin: user?.role === 'super_admin',
      isGroundsCrew: user?.role === 'grounds_crew',
      tenantId,
      tenantPlan,
      availableFeatures,
      hasFeature,
      tenantStatus,
      siteName,
      setSiteName,
      isSiteConfigured,
      plotColors,
      headerLogo,
      loginLogo,
      showPlotSize,
      demoMode,
      portalEnabled,
      googleTagId,
      googleTagEnvOverride,
      refreshSiteSettings: fetchSiteSettings,
      refreshPortalConfig: fetchPortalConfig
    }}>
      {children}
    </AppContext.Provider>
  );
}
